import React from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';

import UploaderDropzone from './UploaderDropzone';
import {
    isPromise,
    getActiveUploaderElementType,
    getActiveDropzoneElement,
} from './utils';
import {
    DRAG_START_EVENT,
    DRAG_END_EVENT,
    WINDOW_DRAG_START_EVENT,
    WINDOW_DRAG_END_EVENT,
    PREVIEW_ELEMENT_TYPE,
    LOADING_ELEMENT_TYPE,
    DROPZONE_ELEMENT_TYPE,
} from './constants';
import { hasDragAndDropFile } from '@eb/eds-utils';

import { NotificationBar } from '@eb/eds-notification';
import { TYPE_ERROR } from '@eb/eds-notification';

const DRAG_EVENT_TO_STATE_MAP = {
    [DRAG_START_EVENT]: { isDraggedOver: true },
    [DRAG_END_EVENT]: { isDraggedOver: false },
    [WINDOW_DRAG_START_EVENT]: { windowDraggedOver: true },
    [WINDOW_DRAG_END_EVENT]: { windowDraggedOver: false },
};

/**
 * Markup that's shown to user by default if their browser
 * doesn't support drag-drop to upload
 */
const defaultUnsupportedFallback = (
    <div>{gettext('Uploading not supported')}</div>
);

/**
 * Markup for error message (if one is provided)
 */
const ErrorMessage = ({ message }) => (
    <div className="eds-l-mar-bot-3">
        <NotificationBar type={TYPE_ERROR}>{message}</NotificationBar>
    </div>
);

/**
 * A utility that manages uploader presentation based on current
 * state.  This includes state of user interaction and state of
 * persisted resources.
 * @extends React.PureComponent
 * @example
 * // <UploaderLayoutManager
 * //     onReceiveFile={uploadFunction}
 * //     dropzoneElement={<MyDropzone />}
 * //     dragDropzoneElement={<MyDraggedDropzone />}
 * //     loadingElement={<MyLoadingLayout />}
 * //     previewElement={<MyPreviewLayout />}
 * // />
 */
export default class UploaderLayoutManager extends React.PureComponent {
    static propTypes = {
        /**
         * Function that's invoked when the user has chosen a file
         */
        onReceiveFile: PropTypes.func.isRequired,

        /**
         * The element that's shown to users when the uploader has
         * no existing file selected and is not having a file dragged
         * over.  This is the initial uploader state.
         */
        dropzoneElement: PropTypes.element.isRequired,

        /**
         * The element that's shown to users when they drag
         * a file over the dropzone.
         */
        dragDropzoneElement: PropTypes.element.isRequired,

        /**
         * The element that's shown to users after they've dropped
         * a file while it is being transported/saved.
         */
        loadingElement: PropTypes.element.isRequired,

        /**
         * The element that's shown to users after a file has been
         * successfully uploaded and persisted.  This is used
         * while the uploader is populated.
         */
        previewElement: PropTypes.element.isRequired,

        /**
         * The element that's shown to users after a file has been
         * dragged over the window, but not yet over the dropzone.
         * This acts as a call-to-action, showing the user where
         * they can drop their file.  If this prop is omitted,
         * the regular dragDropzoneElement is used.
         */
        windowDragDropzoneElement: PropTypes.element,

        /**
         * The consumer must inform the layoutManager if it has
         * the correct data necessary to show a preview of the
         * populated file.
         */
        showPreview: PropTypes.bool,

        /**
         * Any error message the consumer may need to display.
         * This will most often be used to show transport failures
         * (ajax failed, etc) and validation errors (incorrect
         * file types, filesize too large, etc)
         */
        errorContent: PropTypes.node,

        /**
         * This is the component shown to user if their browser
         * doesn't have the correct capabilities to handle
         * drag-drop to upload.
         */
        unsupportedFallback: PropTypes.element,

        /**
         * React ref to externally trigger file upload click event
         */
        fileInputRef: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.shape({ current: PropTypes.any }),
        ]),
    };

    static defaultProps = {
        showPreview: false,
        unsupportedFallback: defaultUnsupportedFallback,
    };

    state = {
        isDraggedOver: false,
        windowDraggedOver: false,
        isLoading: false,
    };

    _handleDragEvent(eventType) {
        const stateChange = DRAG_EVENT_TO_STATE_MAP[eventType];

        if (stateChange) {
            this.setState(stateChange);
        }
    }

    _handleFileDrop(file) {
        const { onReceiveFile } = this.props;
        const finishLoading = () => this.setState({ isLoading: false });

        this.setState({
            isLoading: true,
            isDraggedOver: false,
            windowDraggedOver: false,
        });

        const promise = onReceiveFile(file);

        if (!isPromise(promise)) {
            throw new Error('onReceiveFile prop should return a promise');
        }

        promise.then(finishLoading).catch(finishLoading);
    }

    _getCurrentElement() {
        const {
            dropzoneElement,
            dragDropzoneElement,
            windowDragDropzoneElement,
            previewElement,
            showPreview,
            loadingElement,
            fileInputRef,
        } = this.props;

        const { isLoading, isDraggedOver, windowDraggedOver } = this.state;

        const [dropzoneContent, isDropzone] = getActiveDropzoneElement(
            isDraggedOver,
            windowDraggedOver,
            dropzoneElement,
            dragDropzoneElement,
            windowDragDropzoneElement,
        );

        const elementMap = {
            [PREVIEW_ELEMENT_TYPE]: previewElement,
            [LOADING_ELEMENT_TYPE]: loadingElement,
            [DROPZONE_ELEMENT_TYPE]: (
                <UploaderDropzone
                    onDragEvent={this._handleDragEvent.bind(this)}
                    onFileDrop={this._handleFileDrop.bind(this)}
                    fileInputRef={fileInputRef}
                    isDropzone={isDropzone}
                >
                    {dropzoneContent}
                </UploaderDropzone>
            ),
        };

        const elementType = getActiveUploaderElementType(
            showPreview,
            isLoading,
        );

        return elementMap[elementType];
    }

    render() {
        if (!hasDragAndDropFile()) {
            const { unsupportedFallback } = this.props;

            return unsupportedFallback;
        }

        const { errorContent } = this.props;

        let errorDisplay = null;

        if (errorContent) {
            errorDisplay = <ErrorMessage message={errorContent} />;
        }

        const currentElement = this._getCurrentElement();

        return (
            <div>
                {errorDisplay}
                {currentElement}
            </div>
        );
    }
}
